import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import * as Tooltip from '@radix-ui/react-tooltip'
import { HelpCircle, Search, X, Loader2 } from 'lucide-react'

import { dramaApi, pickLocalized } from '@/lib/api/drama'
import type { DramaCharacter, DramaLang, DramaSegment, LocalizedText } from '@/types/drama'
import { AuthedVideo } from '@/components/drama/AuthedVideo'
import { isUnnamed } from '@/components/drama/CharacterTable'
import { dramaT } from '@/components/drama/i18n'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface Props {
  projectId: string
  availableCharacters: DramaCharacter[]
  initialSegments: DramaSegment[]
  lang: DramaLang
}

const PLOT_OPTIONS = [
  'revenge', 'public_conflict', 'reveal', 'confession', 'betrayal', 'family',
  'comedy', 'tension',
]
const EMOTION_OPTIONS = ['anger', 'warm', 'tension', 'romance', 'sadness', 'surprise', 'comedy']

interface VlmInfo {
  // scene_description is now bilingual ({en,zh}); older data may still be a
  // plain string — pickLocalized tolerates both.
  scene_description?: LocalizedText
  shot_type?: string
  lighting?: string
  visual_quality_score?: number
  is_safe_for_ads?: boolean
  visible_characters?: Array<{ role?: string; position?: string; expression?: string }>
}

function SegmentVlmInfo({ metadata, lang }: { metadata?: Record<string, any>; lang: DramaLang }) {
  const tr = dramaT(lang)
  const vlm: VlmInfo | undefined = metadata?.vlm
  if (!vlm) return null
  return (
    <div className="border-t pt-3 mt-2 space-y-2">
      <div className="text-xs font-semibold text-spectrum-magenta">{tr('seg.visualAnalysis')}</div>
      <p className="text-xs">{pickLocalized(vlm.scene_description, lang)}</p>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {vlm.shot_type && <span>🎥 {vlm.shot_type}</span>}
        {vlm.lighting && <span>💡 {vlm.lighting}</span>}
        {typeof vlm.visual_quality_score === 'number' && (
          <span>⭐ {vlm.visual_quality_score.toFixed(1)}/10</span>
        )}
        {vlm.is_safe_for_ads === false && (
          <span className="text-destructive">{tr('seg.unsafe')}</span>
        )}
      </div>
      {vlm.visible_characters && vlm.visible_characters.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {vlm.visible_characters.map((c, i) => (
            <Badge key={i} variant="secondary">
              {c.role || '角色'}
              {c.position && ` · ${c.position}`}
              {c.expression && ` · ${c.expression}`}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

function TagChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
        active
          ? 'bg-spectrum-magenta/15 border-spectrum-magenta text-spectrum-magenta'
          : 'border-border hover:bg-accent'
      }`}
    >
      {children}
    </button>
  )
}

// "?" hint explaining the 🪝 hook / ⚡ conflict scores on each segment card.
function ScoreHint({ text }: { text: string }) {
  return (
    <Tooltip.Provider delayDuration={120}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span
            role="img"
            aria-label={text}
            onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
            className="inline-flex items-center text-muted-foreground/50 hover:text-muted-foreground"
          >
            <HelpCircle className="w-3 h-3" />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={6}
            className="z-50 max-w-[15rem] rounded-md bg-foreground px-2.5 py-1.5 text-xs leading-snug text-background shadow-md"
          >
            {text}
            <Tooltip.Arrow className="fill-foreground" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

export function SegmentSearchPanel({ projectId, availableCharacters, initialSegments, lang }: Props) {
  const tr = dramaT(lang)
  const [plotTags, setPlotTags] = useState<string[]>([])
  const [emotionTags, setEmotionTags] = useState<string[]>([])
  const [characterCodes, setCharacterCodes] = useState<string[]>([])
  const [episodeNumbers, setEpisodeNumbers] = useState<number[]>([])
  const [minHook, setMinHook] = useState<string>('')
  const [textQuery, setTextQuery] = useState<string>('')

  const [results, setResults] = useState<DramaSegment[]>(initialSegments)
  const [previewSeg, setPreviewSeg] = useState<DramaSegment | null>(null)

  // Derive the list of episode numbers that actually appear in the loaded
  // segments so the filter chip row only shows episodes that have something
  // to filter. Plus per-episode segment counts for a quick hint.
  const episodeOptions = useMemo(() => {
    const counts = new Map<number, number>()
    for (const s of initialSegments) {
      counts.set(s.episode_number, (counts.get(s.episode_number) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => a - b)
      .map(([epNum, count]) => ({ epNum, count }))
  }, [initialSegments])

  const searchMutation = useMutation({
    mutationFn: () =>
      dramaApi.searchSegments(projectId, {
        plot_tags_any: plotTags.length ? plotTags : undefined,
        emotion_tags_any: emotionTags.length ? emotionTags : undefined,
        character_codes: characterCodes.length ? characterCodes : undefined,
        episode_numbers: episodeNumbers.length ? episodeNumbers : undefined,
        min_hook_score: minHook ? parseFloat(minHook) : undefined,
        text_query: textQuery || undefined,
        limit: 200,
      }),
    onSuccess: (data) => setResults(data),
  })

  const clearFilters = () => {
    setPlotTags([])
    setEmotionTags([])
    setCharacterCodes([])
    setEpisodeNumbers([])
    setMinHook('')
    setTextQuery('')
    setResults(initialSegments)
  }

  const hasFilters = useMemo(
    () =>
      plotTags.length ||
      emotionTags.length ||
      characterCodes.length ||
      episodeNumbers.length ||
      minHook ||
      textQuery,
    [plotTags, emotionTags, characterCodes, episodeNumbers, minHook, textQuery],
  )

  // Episode filter applies client-side too so toggling chips gives instant
  // feedback without needing to hit "Search". The server-side filter (sent via
  // searchSegments) is the authoritative one for fresh queries.
  const displayedResults = useMemo(() => {
    let arr = results
    if (episodeNumbers.length > 0) {
      const set = new Set(episodeNumbers)
      arr = arr.filter((s) => set.has(s.episode_number))
    }
    // [REFACTOR-2026-06-08] Keep the whole-episode special segments grouped at
    // the bottom of the list (stable partition).
    const normal = arr.filter((s) => !s.metadata?.full_episode)
    const full = arr.filter((s) => Boolean(s.metadata?.full_episode))
    return [...normal, ...full]
  }, [results, episodeNumbers])

  return (
    <div className="grid grid-cols-[280px_1fr] gap-6">
      {/* Sidebar filters */}
      <aside className="space-y-5 p-4 border rounded-lg bg-card sticky top-4 self-start">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">{tr('seg.filters')}</h3>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-3.5 h-3.5 mr-1" /> {tr('seg.clear')}
            </Button>
          )}
        </div>

        {episodeOptions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs">{tr('seg.episodes')}</Label>
              {episodeNumbers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setEpisodeNumbers([])}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {tr('seg.reset')}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <TagChip
                active={episodeNumbers.length === 0}
                onClick={() => setEpisodeNumbers([])}
              >
                {tr('seg.all')}
              </TagChip>
              {episodeOptions.map(({ epNum, count }) => (
                <TagChip
                  key={epNum}
                  active={episodeNumbers.includes(epNum)}
                  onClick={() =>
                    setEpisodeNumbers((prev) =>
                      prev.includes(epNum)
                        ? prev.filter((x) => x !== epNum)
                        : [...prev, epNum],
                    )
                  }
                >
                  EP {epNum}
                  <span className="ml-1 opacity-60">({count})</span>
                </TagChip>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label className="text-xs">{tr('seg.plotTags')}</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {PLOT_OPTIONS.map((t) => (
              <TagChip
                key={t}
                active={plotTags.includes(t)}
                onClick={() =>
                  setPlotTags((prev) =>
                    prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                  )
                }
              >
                {t}
              </TagChip>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">{tr('seg.emotionTags')}</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {EMOTION_OPTIONS.map((t) => (
              <TagChip
                key={t}
                active={emotionTags.includes(t)}
                onClick={() =>
                  setEmotionTags((prev) =>
                    prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                  )
                }
              >
                {t}
              </TagChip>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">{tr('seg.characters')}</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {availableCharacters.filter((c) => !isUnnamed(c)).map((c) => (
              <TagChip
                key={c.code}
                active={characterCodes.includes(c.code)}
                onClick={() =>
                  setCharacterCodes((prev) =>
                    prev.includes(c.code) ? prev.filter((x) => x !== c.code) : [...prev, c.code],
                  )
                }
              >
                {c.display_name}
              </TagChip>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="min_hook" className="text-xs">
            {tr('seg.minHook')}
          </Label>
          <Input
            id="min_hook"
            type="number"
            min={0}
            max={10}
            step={0.5}
            value={minHook}
            onChange={(e) => setMinHook(e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="text_query" className="text-xs">
            {tr('seg.textSearch')}
          </Label>
          <Input
            id="text_query"
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
            placeholder=""
            className="mt-1"
          />
        </div>

        <Button
          className="w-full"
          onClick={() => searchMutation.mutate()}
          disabled={searchMutation.isPending}
        >
          {searchMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {tr('seg.searching')}
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" /> {tr('seg.search')}
            </>
          )}
        </Button>
      </aside>

      {/* Results grid */}
      <main>
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="text-sm font-semibold">
            {tr('seg.count', displayedResults.length)}
            {displayedResults.length !== results.length && (
              <span className="text-muted-foreground font-normal">
                {' '}
                {tr('seg.loadedOf', results.length)}
              </span>
            )}
            {hasFilters && tr('seg.filtered')}
          </h3>
        </div>

        {(() => {
          const fullResults = displayedResults.filter((s) => Boolean(s.metadata?.full_episode))
          const normalResults = displayedResults.filter((s) => !s.metadata?.full_episode)
          const renderCard = (s: DramaSegment) => (
            <button
              key={s.segment_code}
              onClick={() => setPreviewSeg(s)}
              className="text-left p-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <code className="text-xs px-1.5 py-0.5 rounded bg-muted">{s.segment_code}</code>
                  {Boolean(s.metadata?.full_episode) && (
                    <span className="px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-600 dark:text-violet-300 text-[10px] font-medium shrink-0">
                      {tr('srp.fullEpisode')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground shrink-0">
                  EP {s.episode_number} · {s.start_seconds.toFixed(1)}–{s.end_seconds.toFixed(1)}s
                </div>
              </div>
              {(pickLocalized(s.metadata?.summary_i18n, lang) || s.summary) && (
                <p className="text-sm mb-2 line-clamp-2">
                  {pickLocalized(s.metadata?.summary_i18n, lang) || s.summary}
                </p>
              )}
              <div className="flex flex-wrap gap-1 mb-2">
                {s.plot_tags.map((t) => (
                  <Badge key={t} variant="info">
                    {t}
                  </Badge>
                ))}
                {s.emotion_tags.map((t) => (
                  <Badge key={t} variant="warning">
                    {t}
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {s.hook_score !== null && <span>🪝 {s.hook_score.toFixed(1)}</span>}
                {s.conflict_score !== null && <span>⚡ {s.conflict_score.toFixed(1)}</span>}
                {(s.hook_score !== null || s.conflict_score !== null) && (
                  <ScoreHint text={tr('seg.scoreHint')} />
                )}
              </div>
            </button>
          )
          return (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {normalResults.map(renderCard)}
              </div>
              {fullResults.length > 0 && (
                <>
                  <div className="mt-5 mb-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    {tr('seg.fullEpisodes')}
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {fullResults.map(renderCard)}
                  </div>
                </>
              )}
            </>
          )
        })()}
      </main>

      {/* Preview dialog */}
      <Dialog open={!!previewSeg} onOpenChange={(o) => !o && setPreviewSeg(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {previewSeg?.segment_code} · EP {previewSeg?.episode_number}
            </DialogTitle>
          </DialogHeader>
          {previewSeg && (
            <div className="space-y-3">
              <AuthedVideo
                path={dramaApi.segmentFileUrl(projectId, previewSeg.segment_code)}
                stream
                controls
                autoPlay
                className="w-full rounded-md bg-black aspect-[9/16] max-h-[60vh]"
              />
              <p className="text-sm">
                {pickLocalized(previewSeg.metadata?.summary_i18n, lang) || previewSeg.summary}
              </p>
              {(pickLocalized(previewSeg.metadata?.transcript_i18n, lang) || previewSeg.transcript) && (
                <p className="text-xs text-muted-foreground italic">
                  {pickLocalized(previewSeg.metadata?.transcript_i18n, lang) || previewSeg.transcript}
                </p>
              )}
              <SegmentVlmInfo metadata={previewSeg.metadata ?? undefined} lang={lang} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
